import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { faChartPieSimple, faMemoPad, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { faCaretDown, faCaretUp, faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import {
	AvatarProfile,
	ButtonIcon,
	ButtonStandard,
	Chip,
	InputNumeric,
	Text,
	TextEditor,
	Tooltip,
} from "@performyard/py-components";
import type { ICellRendererParams, TextEditorRef } from "@performyard/py-components";
import type { BudgetDecisionData } from "@performyard/service-contracts";
import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow, DecisionRowApprovalSnapshot, MyDecisionsDirectReportRow } from "../../types";
import { DecisionTableContext, IncreaseValues } from "../MyDecisions/types";

import { useCycleOwnerEditBudgetDecision, useRbacPrimaryRolePermissions } from "../../../../api/hooks";

import { formatConditionalDecimals, formatCurrencyFull } from "../../utils";
import { CycleBudgetOverviewTableRow } from "../CycleOverview/utils";
import { pluralize } from "../SharedComponents/utils";

import { useIncreaseValue, useIsEditingRow } from "../MyDecisions/decision-store";

const coerceBudgetIncreaseNumber = (v: number | "" | null | undefined): number => {
	if (v === "" || v === null || v === undefined) return 0;
	return typeof v === "number" && Number.isFinite(v) ? v : 0;
};

const isDraftPercentMissing = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const p = increaseValues[rowId]?.percent;
	return p === undefined || p === null || p === "";
};

const isDraftAmountMissing = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const a = increaseValues[rowId]?.amount;
	return a === undefined || a === null || a === "";
};

const NOTES_MAX_LENGTH = 800;

/** Stable string for PATCH `notesByDecisionRowId` keys (direct-report `employee_id`) */
export const notesPayloadKey = (r: DecisionRow): string => String(r.employee_id ?? "");

const mapApprovalToSnapshot = (a: MyDecisionsDirectReportRow["approvals"][number]): DecisionRowApprovalSnapshot => ({
	approver_id: String(a.approverId),
	approver_name: a.approverName,
	sequence: a.sequence,
	status: a.status,
	decided_at: a.decidedAt != null ? String(a.decidedAt) : null,
	notes: a.notes ?? null,
	amount_at_approval: a.amountAtApproval,
	updated_at: a.updatedAt != null ? String(a.updatedAt) : null,
	proposed_increase_amount: a.proposedIncreaseAmount ?? null,
	proposed_increase_percent: a.proposedIncreasePercent ?? null,
	proposed_salary: a.proposedSalary ?? null,
});

export const toDecisionRow = (
	dr: MyDecisionsDirectReportRow,
	index: number,
	userID: string | undefined
): DecisionRow => {
	const approvalEntry =
		userID === undefined || dr.approvals.length === 0
			? null
			: (dr.approvals.find((a) => String(a.approverId) === userID) ?? null);

	const approvalEntries = dr.approvals.map(mapApprovalToSnapshot);

	return {
		_id: String(dr.id),
		compensation_cycle_id: String(dr.compensationCycle),
		employee_id: String(dr.employeeId),
		employee_name: dr.name || `Employee ${index + 1}`,
		employee_title: dr.jobTitle,
		current_salary: dr.currentSalary,
		proposed_increase_percent: dr.proposedIncreasePercent,
		proposed_increase_amount: dr.proposedIncreaseAmount,
		proposed_salary: dr.proposedSalary,
		status: dr.status,
		notes: dr.myDecisionNotes ?? approvalEntry?.notes ?? null,
		approver: approvalEntry?.approverId != null ? String(approvalEntry.approverId) : null,
		updatedAt: dr.updatedAt ?? approvalEntry?.updatedAt ?? null,
		approval_entries: approvalEntries.length > 0 ? approvalEntries : undefined,
	};
};

/** Cycle `allotment_increase_percent` surfaced as grid context `recommendedIncreasePercent` — display only. */
export const SuggestedIncreaseCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || !context || row.superRow) {
		return null;
	}
	const label = `${formatConditionalDecimals(context.recommendedIncreasePercent)}%`;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="semi-bold" color="green-5">
				{label}
			</Text>
		</div>
	);
};

export const EmployeeNameCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext;
	if (!row) return null;
	const isExpanded = context.expandedRowId === row?._id;

	const handleToggle = () => {
		if (isExpanded) {
			context.onCollapse(row._id);
		} else {
			context.onExpand(row._id);
		}
	};

	return (
		<div className="flex h-full items-center gap-2">
			<ButtonIcon
				variant="ghost"
				color="neutral"
				size={16}
				icon={isExpanded ? faCaretUp : faCaretDown}
				onClick={(e) => {
					e.stopPropagation();
					handleToggle();
				}}
				aria-label={isExpanded ? "Collapse Notes" : "Expand Notes"}
			/>
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					wrap={false}
					dangerouslySetClassName="truncate"
				>
					{row.employee_name}
				</Text>
				<Text size="caption" weight="regular" color="neutral-7">
					{row.employee_title}
				</Text>
			</div>
		</div>
	);
};

export const ManagerNameCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<AvatarProfile employeeId={row.managerId} employeeName={row.managerName} noLink={true}></AvatarProfile>
			</div>
		</div>
	);
};

export const ActingManagerNameCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	if (!row?.actingManagerId || row?.actingManagerId === row.managerId) {
		return null;
	}

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Chip
					label="Acting Manager"
					employeeId={row.actingManagerId}
					employeeName={row.actingManagerName ?? ""}
					color="neutral"
					size={24}
					employeeTextSize={"caption"}
				/>
			</div>
		</div>
	);
};

export const BudgetStatusCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	const statusColorMap = {
		Submitted: "yellow",
		"Not Submitted": "neutral",
		Approved: "green",
	};

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Chip label={row.status} color={statusColorMap[row.status]} size={24} />
			</div>
		</div>
	);
};

export const DirectReportCountCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text size="caption" weight="regular" color="neutral-7">
					{row?.managerDirectReportCount}{" "}
					{pluralize(row?.managerDirectReportCount ?? 0, "Direct Report", "Direct Reports")}
				</Text>
			</div>
		</div>
	);
};

export const BudgetCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text size="caption" weight="regular" color="neutral-7">
					{formatCurrencyFull(row?.budget)}
				</Text>
			</div>
		</div>
	);
};

const statusToQuickFilter: Record<string, string> = {
	"Not Submitted": "not-submitted",
	Submitted: "submitted",
	Approved: "approved",
};

export const ViewBudgetButtonCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	const navigate = useNavigate();
	const { cycleId } = useParams<{ cycleId: string }>();
	if (!row) return null;

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="flex w-[70px] flex-col justify-center gap-0.5">
				<ButtonStandard
					color="neutral"
					variant="outline"
					size="small"
					onClick={() => {
						navigate(
							`/compensation/cycles/${cycleId}/budget-reviews?search=${encodeURIComponent(row.managerName)}&quickFilter=${statusToQuickFilter[row.status]}`
						);
					}}
				>
					View
				</ButtonStandard>
			</div>
		</div>
	);
};

export const CurrentSalaryCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row) return null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="regular">
				{formatCurrencyFull(row.current_salary)}
			</Text>
		</div>
	);
};

export const IncreasePercentCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	if (!row || !context) return null;

	const rawContextValue = (storeValue ?? context.increaseValues[row._id])?.percent;
	const displayValue: number | null = typeof rawContextValue === "number" ? toFixed(rawContextValue, 2) : null;
	const rowStatus = row.status || "pending";
	const isInlineEditing = context.store ? storeIsEditing : context.editingRowIds?.has(row._id) === true;

	const showValidationError =
		!context.readOnlyBudgetReview &&
		!isInlineEditing &&
		rowStatus === "pending" &&
		!row.superRow &&
		String(context.groupCannotBeSubmitted ?? "") === String(context.managerId) &&
		isDraftPercentMissing(context.increaseValues, row._id);

	if (!isInlineEditing && (context.readOnlyBudgetReview || rowStatus !== "pending")) {
		return row.proposed_increase_percent != null ? (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{formatConditionalDecimals(row.proposed_increase_percent)}%
				</Text>
			</div>
		) : (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					—
				</Text>
			</div>
		);
	}

	return (
		<div
			className="flex h-full items-center gap-1"
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<InputNumeric
				id={`increase-percent-${row._id}`}
				data-testid={`increase-percent-${row._id}`}
				value={displayValue}
				allowEmpty={true}
				onChange={(val) => {
					if (val == null) {
						context.onPercentChange(row._id, "");
						return;
					}
					context.onPercentChange(row._id, val);
				}}
				onFocus={() => {
					if (displayValue == null) {
						context.onPercentChange(row._id, context.recommendedIncreasePercent);
					}
				}}
				precision={2}
				step={1}
				placeholder="-"
				errored={showValidationError}
				dangerouslySetClassName="w-[82px]"
				size={"small"}
			/>
			<span className="text-sm text-neutral-500">%</span>
		</div>
	);
};

export const IncreaseAmountCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	if (!row || !context) return null;

	const rawContextValue = (storeValue ?? context.increaseValues[row._id])?.amount;
	const displayValue: number | null = typeof rawContextValue === "number" ? toFixed(rawContextValue, 2) : null;
	const rowStatus = row.status || "pending";
	const isInlineEditing = context.store ? storeIsEditing : context.editingRowIds?.has(row._id) === true;

	const showValidationError =
		!context.readOnlyBudgetReview &&
		!isInlineEditing &&
		rowStatus === "pending" &&
		!row.superRow &&
		String(context.groupCannotBeSubmitted ?? "") === String(context.managerId) &&
		isDraftAmountMissing(context.increaseValues, row._id);

	if (!isInlineEditing && (context.readOnlyBudgetReview || rowStatus !== "pending")) {
		return row.proposed_increase_amount != null ? (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{formatCurrencyFull(row.proposed_increase_amount)}
				</Text>
			</div>
		) : (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					—
				</Text>
			</div>
		);
	}

	return (
		<div
			className="flex h-full items-center gap-1"
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<span className="text-sm text-neutral-500">$</span>
			<InputNumeric
				id={`increase-amount-${row._id}`}
				data-testid={`increase-amount-${row._id}`}
				value={displayValue}
				allowEmpty={true}
				onChange={(val) => {
					if (val == null) {
						context.onAmountChange(row._id, "");
						return;
					}
					context.onAmountChange(row._id, val);
				}}
				onFocus={() => {
					if (displayValue == null) {
						context.onPercentChange(row._id, context.recommendedIncreasePercent);
					}
				}}
				precision={2}
				step={1}
				placeholder="-"
				errored={showValidationError}
				dangerouslySetClassName="w-[82px]"
				size={"small"}
			/>
		</div>
	);
};

export const NewSalaryCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	if (!row || !context) return null;
	const increaseAmount = (storeValue ?? context.increaseValues[row._id])?.amount;
	const hasValue = increaseAmount != null && increaseAmount !== "";
	const newSalary = hasValue ? row.current_salary + (increaseAmount as number) : null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="regular" color={newSalary != null ? undefined : "neutral-6"}>
				{newSalary != null ? formatCurrencyFull(newSalary) : "—"}
			</Text>
		</div>
	);
};

const NotesButton = ({ params, readOnly }: { params: ICellRendererParams<DecisionRow>; readOnly?: boolean }) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || row.superRow) return null;
	const notesExpanded = context?.expandedRowId === row._id;
	const noteExists = Boolean(row?.notes);
	const notes = row?.notes ?? null;
	const toggleNotesRow = () => {
		if (!context) return;
		if (context.expandedRowId === row._id) {
			context.onCollapse(row._id);
		} else {
			context.onExpand(row._id);
		}
	};
	const placement = notes && notes.length >= 400 ? "left" : "top";
	if (readOnly) {
		return (
			<Tooltip content={notes} placement={placement} disabled={!notes}>
				<ButtonIcon
					icon={faMemoPad}
					color={noteExists ? "primary" : "neutral"}
					onClick={() => {}}
					aria-label={`Notes for ${row.employee_name}`}
					size={20}
					disabled={readOnly}
				/>
			</Tooltip>
		);
	}
	return (
		<Tooltip content={`Notes about ${row.employee_name}`} placement="top">
			<ButtonIcon
				icon={faMemoPad}
				color={noteExists ? "primary" : "neutral"}
				onClick={(e) => {
					e.stopPropagation();
					toggleNotesRow();
				}}
				aria-expanded={notesExpanded}
				aria-label={`${notesExpanded ? "Close" : "Open"} notes for ${row.employee_name}`}
				size={20}
			/>
		</Tooltip>
	);
};

const GoalsButton = ({ row, readOnly }: { row: DecisionRow; readOnly?: boolean }) => {
	if (readOnly) {
		return (
			<ButtonIcon
				color={"primary"}
				icon={faChartPieSimple}
				disabled={readOnly}
				href={`/employees/${row.employee_id}/goals/`}
				aria-label={`${row.employee_name}'s Goals`}
				size={20}
			/>
		);
	}
	return (
		<Tooltip content={`${row.employee_name}'s Goals`} placement="top">
			<ButtonIcon
				color={"primary"}
				icon={faChartPieSimple}
				href={`/employees/${row.employee_id}/goals/`}
				aria-label={`${row.employee_name}'s Goals`}
				size={20}
			/>
		</Tooltip>
	);
};

const ReviewsButton = ({ row, readOnly }: { row: DecisionRow; readOnly?: boolean }) => {
	if (readOnly) {
		return (
			<ButtonIcon
				color={"primary"}
				icon={faPenToSquare}
				disabled={readOnly}
				href={`/reviews/${row.employee_id}/reviews-of-me/`}
				aria-label={`${row.employee_name}'s Reviews`}
				size={20}
			/>
		);
	}
	return (
		<Tooltip content={`${row.employee_name}'s Reviews`} placement="top">
			<ButtonIcon
				color={"primary"}
				icon={faPenToSquare}
				href={`/reviews/${row.employee_id}/reviews-of-me/`}
				aria-label={`${row.employee_name}'s Reviews`}
				size={20}
			/>
		</Tooltip>
	);
};

export const EmployeeInfoCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row || row.superRow) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<NotesButton params={params} />
			<GoalsButton row={row} />
			<ReviewsButton row={row} />
		</div>
	);
};

export const EmployeeInfoCellRendererBudgetReviews = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row || row.superRow) return null;

	const { data: permissions } = useRbacPrimaryRolePermissions("compensation");
	const readOnly = !permissions?.includes("compensation:useInfoLinks");

	return (
		<div className="flex h-full items-center gap-3">
			<NotesButton params={params} readOnly={true} />
			<GoalsButton row={row} readOnly={readOnly} />
			<ReviewsButton row={row} readOnly={readOnly} />
		</div>
	);
};

export const SuperRowRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const notesValuesRef = context?.notesValuesRef;
	const activeNoteEditorRef = context?.activeNoteEditorRef;
	/** Set on super rows to `employee_id` so it matches `notesByDecisionRowId` */
	const noteRowId = row?.parentRowId ?? "";
	const serverNotesText = row?.notes != null ? String(row.notes) : "";
	const hasServerNotes = serverNotesText.length > 0;
	const onNotesChange = context?.onNotesChange;
	const editorRef = useRef<TextEditorRef>(null);

	const [notesDraft, setNotesDraft] = useState(() => {
		if (noteRowId && notesValuesRef && noteRowId in notesValuesRef.current) {
			return notesValuesRef.current[noteRowId];
		}
		return serverNotesText;
	});

	// Row data from the grid is server-sourced; drafts live in `notesValuesRef`. After AG Grid
	// `refreshCells({ force: true })` (e.g. when increase fields change elsewhere), this cell remounts
	// and must rehydrate from the ref or autosave appears broken for non-first groups.
	useEffect(() => {
		if (!noteRowId || !notesValuesRef) {
			setNotesDraft(serverNotesText);
			return;
		}
		if (noteRowId in notesValuesRef.current) {
			setNotesDraft(notesValuesRef.current[noteRowId]);
			return;
		}
		setNotesDraft(serverNotesText);
	}, [serverNotesText, noteRowId, notesValuesRef]);

	// Restore focus when this component remounts while the user was actively editing.
	// When AG Grid refreshes the full-width row (e.g. after autosave triggers a re-render),
	// the component unmounts and remounts, causing the TextEditor to lose focus.
	useEffect(() => {
		if (activeNoteEditorRef?.current === noteRowId && noteRowId) {
			// Defer focus to next frame so the TipTap editor is fully initialized
			const raf = requestAnimationFrame(() => {
				editorRef.current?.focus();
			});
			return () => cancelAnimationFrame(raf);
		}
	}, [activeNoteEditorRef, noteRowId]);

	const handleNotesEditorChange = useCallback(
		(value: string) => {
			const val = value.slice(0, NOTES_MAX_LENGTH);
			setNotesDraft(val);
			if (activeNoteEditorRef && noteRowId) {
				activeNoteEditorRef.current = noteRowId;
			}
			if (noteRowId) onNotesChange?.(noteRowId, val);
		},
		[noteRowId, onNotesChange, activeNoteEditorRef]
	);

	const handleEditorBlur = useCallback(
		(event: FocusEvent) => {
			if (!activeNoteEditorRef || activeNoteEditorRef.current !== noteRowId) return;
			if (event.relatedTarget != null) {
				// Focus moved to a real element — user intentionally left the editor
				activeNoteEditorRef.current = null;
			} else {
				// relatedTarget is null: either AG Grid is remounting the row (focus will be
				// restored on remount) or the user clicked non-focusable space. Use a short
				// delay to let the remount happen; if it doesn't, clear the ref.
				setTimeout(() => {
					if (activeNoteEditorRef.current === noteRowId) {
						activeNoteEditorRef.current = null;
					}
				}, 100);
			}
		},
		[activeNoteEditorRef, noteRowId]
	);

	if (!row || !context) return null;
	return (
		<div
			className="flex flex-col gap-2 px-6"
			style={{ padding: "16px 24px" }}
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<Text size="paragraph-3" weight="semi-bold">
				Notes
			</Text>
			<div className="flex flex-col gap-1">
				<TextEditor
					ref={editorRef}
					key={noteRowId}
					id={`${noteRowId}_notes`}
					onChange={handleNotesEditorChange}
					onBlur={handleEditorBlur}
					initialValue={notesDraft}
					placeholder={!hasServerNotes ? `Add notes about ${row.employee_name}` : undefined}
					characterLimit={NOTES_MAX_LENGTH}
					minHeight={140}
					readOnly={row.status !== "pending"}
					dangerouslySetClassName="h-[120px] w-[1216px] resize-none rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-5 focus:outline-none"
				/>
			</div>
		</div>
	);
};

export const EmployeeAvatarCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	if (!row || row.superRow) {
		return null;
	}

	return (
		<div className="flex h-full min-w-0 items-center">
			<AvatarProfile employeeId={row.employee_id} employeeName={row.employee_name ?? ""} noLink={true} />
		</div>
	);
};

export const BudgetReviewRowEditCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;

	const context = params.context as DecisionTableContext;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	const isEditingThisRow = context?.store
		? Boolean(row && !row.superRow && storeIsEditing)
		: Boolean(row && !row.superRow && context.editingRowIds?.has(row._id));
	const newEditId = row?._id ?? "";
	const cycleId = row?.compensation_cycle_id || "";
	const increaseVal = storeValue ?? context.increaseValues[newEditId];
	const noChange = toFixed(row?.proposed_increase_amount || 0, 2) === toFixed(increaseVal?.amount || 0, 2);
	const canEdit = Boolean(cycleId && newEditId && row && !noChange);
	const budgetDecisionData: BudgetDecisionData =
		row && canEdit
			? {
					employeeBeingUpdated: row.employee_id,
					increasePercent: coerceBudgetIncreaseNumber(increaseVal?.percent),
					increaseAmount: coerceBudgetIncreaseNumber(increaseVal?.amount),
					currentSalary: row?.current_salary ?? 0,
				}
			: { employeeBeingUpdated: "", increasePercent: 0, increaseAmount: 0, currentSalary: 0 };
	const { mutate: editBudgetDecisionRow } = useCycleOwnerEditBudgetDecision(cycleId, budgetDecisionData);

	if (!row || row.superRow) {
		return null;
	}
	const handleEdit = () => {
		if (canEdit) {
			editBudgetDecisionRow();
		} else {
			context.onBudgetReviewCancelEditRow?.(row);
		}
	};
	return (
		<div className="flex h-full items-center justify-end pr-1">
			{!isEditingThisRow && (
				<ButtonIcon
					variant="standard"
					color="neutral"
					shade={3}
					size={20}
					icon={faPencil}
					onClick={() => {
						context.onBudgetReviewEditRow?.(row);
					}}
					aria-label={`Edit ${row.employee_name}`}
				/>
			)}
			{isEditingThisRow && (
				<div className="inline-flex shrink-0 flex-row items-center gap-[2px]">
					<ButtonIcon
						variant="standard"
						color="green"
						shade={5}
						size={20}
						icon={faCircleCheck}
						onClick={handleEdit}
						aria-label={`Edit ${row.employee_name}`}
					/>
					<ButtonIcon
						variant="standard"
						color="red"
						shade={5}
						size={20}
						icon={faCircleXmark}
						onClick={() => {
							context.onBudgetReviewCancelEditRow?.(row);
						}}
						aria-label={`Edit ${row.employee_name}`}
					/>
				</div>
			)}
		</div>
	);
};
